import React from 'react';
import { useMutation } from 'hooks/apollo';
import { THEMES } from 'shared/constants/design';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Input, InputNumber, Radio, Slider, Row, Col, Image, Alert, Checkbox } from 'antd';
import FormLabel from 'components/FormLabel';
import ColorPicker from 'components/ColorPicker';
import HasPermission from 'components/HasPermission';
import LicenseTag from 'components/LicenseTag';
import { SUPER_USER } from 'shared/constants/permissions';
import globalMessages from 'translations/messages';
import removeEmptyValues from 'shared/utils/removeEmptyValues';

import useGlobalContext from 'hooks/useGlobalContext';
import mutation from './updateDesign.gql';
import messages from './messages';

const LOGO_SOURCE_OPTIONS = [{
  value: 'CUSTOMER',
  label: 'logoSourceCustomer',
}, {
  value: 'URL',
  label: 'logoSourceUrl',
}, {
  value: 'NONE',
  label: 'logoSourceNone',
}];

const EditDistributionDesign = (props) => {
  const { formatMessage: $t } = useIntl();
  const { message } = useGlobalContext();

  const {
    id, logo, customTheme, useToggleButtons,
    form, onDone, onChange, activeLicense,
  } = props;

  const theme = props.theme || 'default';

  const initialValues = {
    logoSource: logo.source || (logo.url && 'URL') || 'NONE',
    logoUrl: logo.url,
    logoHeight: logo.height,
    theme,
    customTheme: { ...THEMES[theme], ...removeEmptyValues(customTheme) },
    useToggleButtons,
  };
  const [save] = useMutation(mutation);
  const onFinish = async (values) => {
    try {
      await save({ variables: { id, design: values } });
      message.success($t(messages.saveDesignSuccess));
    } catch (error) {
      message.error($t(messages.saveDesignError));
    }
    onDone?.();
  };

  const onFieldsChange = (changedFields) => {
    const themeChanged = changedFields.find(f => f.name.includes('theme'));
    if (themeChanged) {
      form.setFieldsValue({ customTheme: THEMES[themeChanged.value] });
    }
    if (changedFields.length && !changedFields.find(f => f.name.includes('customCSS'))) {
      onChange();
    }
  };

  const canHaveCustomTheme = activeLicense?.settings.customTheme;
  const disableCustomTheme = !canHaveCustomTheme && theme !== 'custom';

  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={onFinish}
      initialValues={initialValues}
      onFieldsChange={onFieldsChange}
    >
      <Form.Item
        name="logoSource"
        label={<FormattedMessage {...messages.logoSource} />}
      >
        <Radio.Group buttonStyle="solid">
          {LOGO_SOURCE_OPTIONS.map(({ label, value }) => (
            <Radio.Button key={value} value={value}>
              <FormattedMessage {...messages[label]} />
            </Radio.Button>
          ))}
        </Radio.Group>
      </Form.Item>
      <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.logoSource !== curValues.logoSource}>
        {() => (
          <>
            {form.getFieldValue('logoSource') === 'CUSTOMER' && (
              <Form.Item>
                {logo.customerLogoUrl
                  ? <Image src={logo.customerLogoUrl} height={200} />
                  : <Alert type="warning" message={<FormattedMessage {...messages.logoSourceCustomerMissing} />} showIcon />}
              </Form.Item>
            )}
            {form.getFieldValue('logoSource') === 'URL' && (
              <Col sm={24} md={8}>
                <Form.Item name="logoUrl" label={<FormattedMessage {...messages.logoUrl} />}>
                  <Input />
                </Form.Item>
              </Col>
            )}
            {form.getFieldValue('logoSource') !== 'NONE' && (
              <Col sm={24} md={8}>
                <Form.Item name="logoHeight" label={<FormattedMessage {...messages.logoHeight} />}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={value => `${value}px`}
                    parser={value => value.replace('px', '')}
                  />
                </Form.Item>
              </Col>
            )}
          </>
        )}
      </Form.Item>
      <Form.Item
        name="useToggleButtons"
        valuePropName="checked"
        label={<FormLabel label={messages.useToggleButtons} description={messages.useToggleButtonsTooltip} tooltipPlacement="topRight" />}
      >
        <Checkbox>
          <FormattedMessage {...messages.useToggleButtonsLabel} />
        </Checkbox>
      </Form.Item>

      <Form.Item
        name="theme"
        label={<FormLabel label={messages.design} description={messages.customThemeDisclaimer} tooltipPlacement="topLeft" extra={!canHaveCustomTheme && <LicenseTag activeLicense={activeLicense} setting="customTheme" showTooltip popoverProps={{ placement: 'topRight' }} />} />}
      >
        <Radio.Group buttonStyle="solid">
          <Radio.Button value="default">
            <FormattedMessage {...globalMessages.defaultTheme} />
          </Radio.Button>
          <Radio.Button value="dark">
            <FormattedMessage {...globalMessages.darkTheme} />
          </Radio.Button>
          <Radio.Button value="custom" disabled={disableCustomTheme}>
            <FormattedMessage {...globalMessages.customTheme} />
          </Radio.Button>
        </Radio.Group>
      </Form.Item>

      {/* Custom theme */}
      <Form.Item noStyle shouldUpdate={(prevValues, curValues) => prevValues.theme !== curValues.theme}>
        {() => form.getFieldValue('theme') === 'custom' && (
          <>
            <Row>
              {Object.keys(THEMES.default).filter(key => key.indexOf('Color') !== -1).map(colorFormField => (
                <Col key={colorFormField} sm={24} md={6}>
                  <Form.Item name={['customTheme', colorFormField]}>
                    <ColorPicker label={messages[colorFormField] ? <FormattedMessage {...messages[colorFormField]} /> : colorFormField} enableAlpha={false} />
                  </Form.Item>
                </Col>
              ))}
            </Row>

            <Row gutter={16}>
              <Col sm={24} md={12}>
                <Form.Item name={['customTheme', 'cornerRadius']} label={<FormattedMessage {...messages.cornerRadius} />}>
                  <Slider max={16} marks={{ 0: '0px', 16: '16px' }} />
                </Form.Item>
              </Col>
            </Row>

            <HasPermission permission={SUPER_USER}>
              <Form.Item name={['customTheme', 'customCSS']} label={<FormattedMessage {...messages.customCSS} />}>
                <Input.TextArea autoSize={{ minRows: 5 }} onPressEnter={onChange} />
              </Form.Item>
            </HasPermission>
          </>
        )}
      </Form.Item>
    </Form>
  );
};

export default EditDistributionDesign;
